/**
 * The connection to `/api/v1/events/stream`.
 *
 * One `EventSource` per tab, shared by every component that listens, held in
 * module scope rather than in a React context. Two reasons: the connection has
 * to survive a route change and a remount, and hooks that only want to know
 * *whether* it is live - to decide whether they still need to poll - should not
 * have to sit under a provider to ask.
 *
 * Reconnection is layered. `EventSource` retries on its own after a dropped
 * connection, using the `retry:` interval the server sends. It does *not* retry
 * when the server answered with an error status, which is the case that matters
 * here: a 401 while the session is being refreshed, or a 429 from the
 * per-account stream cap. Those close the source for good, so the backoff below
 * reopens it.
 *
 * Nothing that arrives here is trusted as data. An event says something changed;
 * the caller refetches through the ordinary API, which is where permissions are
 * decided.
 */

import { API_URL } from "@/lib/api";

export type RealtimeStatus = "idle" | "connecting" | "live" | "offline";

export interface RealtimeEvent {
  kind: string;
  payload: Record<string, unknown>;
}

type Listener = (event: RealtimeEvent) => void;

/** Named events the server sends. `message` is unused: every frame has a name. */
const EVENT_NAMES = ["ready", "task.assigned", "task.closed", "notification"] as const;

const FIRST_RETRY_MS = 2_000;
const MAX_RETRY_MS = 60_000;
/**
 * Grace period before a connection with no listeners is closed. React's strict
 * mode mounts, unmounts and remounts every effect in development, and without
 * this the stream is torn down and rebuilt on every page load - which shows up
 * as a pair of connect/disconnect lines in the API log for something that never
 * actually disconnected.
 */
const CLOSE_GRACE_MS = 1_000;

const listeners = new Set<Listener>();
const statusWatchers = new Set<() => void>();

let source: EventSource | null = null;
let status: RealtimeStatus = "idle";
let retryDelay = FIRST_RETRY_MS;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function setStatus(next: RealtimeStatus): void {
  if (next === status) return;
  status = next;
  statusWatchers.forEach((watcher) => watcher());
}

function dispatch(kind: string, raw: MessageEvent): void {
  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(raw.data) as Record<string, unknown>;
  } catch {
    // A frame we cannot read is still a signal that something happened, so it is
    // delivered with an empty payload rather than dropped: the listener's first
    // move is to refetch, and that works without knowing what changed.
  }
  listeners.forEach((listener) => listener({ kind, payload }));
}

function clearRetry(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function scheduleRetry(): void {
  if (retryTimer !== null || listeners.size === 0) return;
  const delay = retryDelay;
  retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    open();
  }, delay);
}

function open(): void {
  if (source !== null || listeners.size === 0) return;
  setStatus("connecting");

  // Credentials, not a bearer token: `EventSource` cannot set headers, and the
  // session cookie is first-party because the interface and the API share an
  // origin. `withCredentials` matters only when VITE_API_URL points elsewhere.
  const opened = new EventSource(`${API_URL}/v1/events/stream`, {
    withCredentials: true,
  });
  source = opened;

  opened.onopen = () => {
    retryDelay = FIRST_RETRY_MS;
    setStatus("live");
  };

  opened.onerror = () => {
    if (opened.readyState === EventSource.CLOSED) {
      // The server refused rather than the connection dropping. `EventSource`
      // will not try again by itself, so this layer has to.
      opened.close();
      if (source === opened) source = null;
      setStatus("offline");
      scheduleRetry();
      return;
    }
    // Still CONNECTING: the browser is already retrying on its own.
    setStatus("connecting");
  };

  EVENT_NAMES.forEach((name) => {
    opened.addEventListener(name, (event) => dispatch(name, event as MessageEvent));
  });
}

function close(): void {
  clearRetry();
  if (source !== null) {
    source.close();
    source = null;
  }
  setStatus("idle");
}

/**
 * A tab that has been in the background for a while comes back to a connection
 * the operating system may have quietly killed, or to a backoff timer with
 * another minute to run. Neither is worth waiting out when somebody is looking
 * at the screen again.
 */
function onVisible(): void {
  if (document.visibilityState !== "visible") return;
  if (listeners.size === 0) return;
  if (status === "live" || status === "connecting") return;
  clearRetry();
  retryDelay = FIRST_RETRY_MS;
  open();
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", onVisible);
}

/** Listen for events, opening the shared connection if it is not open yet. */
export function subscribeRealtime(listener: Listener): () => void {
  listeners.add(listener);
  if (closeTimer !== null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
  open();
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0 || closeTimer !== null) return;
    closeTimer = setTimeout(() => {
      closeTimer = null;
      if (listeners.size === 0) close();
    }, CLOSE_GRACE_MS);
  };
}

/** Store contract for `useSyncExternalStore`. */
export function subscribeRealtimeStatus(onChange: () => void): () => void {
  statusWatchers.add(onChange);
  return () => statusWatchers.delete(onChange);
}

export function getRealtimeStatus(): RealtimeStatus {
  return status;
}
